import { showOldNotification as showNotification, Flex, IconV3, RpiButton, RpiText } from "@rpi/openapi-core";
import { GraphQL } from "@rpi/openapi-api";
import { MantineProvider } from "@mantine/core";
import React from "react";
import { AppPage } from "../../../components/AppPage";
import { createTableRowMenuColumn, TableRowMenu } from "../../../components/TableRowMenu";
import { RpiTableContainer } from "../../../components/RpiTableContainer";
import { billingTaxDocumentsColumns } from "../../../configuration/columns/upload.column";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { useNavigateRoute } from "../../../hooks/useNavigateRoute";
import { RpiTable } from "../../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../../theme/rpaTheme";
import { isUndefined } from "../../../utils/type.util";

interface TaxExemptionSectionProps {
  refetchDep: boolean;
}

export const TaxExemptionSection: React.FC<TaxExemptionSectionProps> = ({ refetchDep }) => {
  const user = useAuthUser();
  const navigateRoute = useNavigateRoute();

  const { data, isLoading, refetch } = GraphQL.useCustomerQuery(
    { customerPublicId: user.id! },
    { enabled: Boolean(user.id) }
  );
  const customer = data?.customer;

  const deleteMutation = GraphQL.useUploadedFileDeleteMutation<{ message?: string }>({
    onSuccess: () => refetch(),
    onError: (error) => {
      showNotification({
        title: "Failed to delete tax document.",
        message: error instanceof Error ? error.message : "Please try again.",
        color: "red",
      });
    },
  });

  React.useEffect(() => {
    if (refetchDep) {
      void refetch();
    }
  }, [refetchDep, refetch]);

  const taxStatusLabel = customer?.chargeTax ? "Not tax exempt" : "Tax exempt";
  const hasTaxDocuments = Boolean(customer?.taxDocuments?.length);

  const deleteTaxDocument = deleteMutation.mutate;
  const taxDocumentRowMenuColumns = React.useMemo(
    () => [
      createTableRowMenuColumn<Pick<GraphQL.UploadedFileResponse, "id" | "url">>((row) => (
        <TableRowMenu>
          {row.url && (
            <TableRowMenu.Item
              icon={<IconV3 icon="description" size={20} />}
              onClick={() => window.open(row.url!, "_blank", "noopener,noreferrer")}
            >
              View
            </TableRowMenu.Item>
          )}
          <TableRowMenu.Item
            danger
            icon={<IconV3 icon="delete" size={20} color="inherit" />}
            onClick={() => row.id && deleteTaxDocument({ id: row.id })}
          >
            Delete
          </TableRowMenu.Item>
        </TableRowMenu>
      )),
    ],
    [deleteTaxDocument]
  );

  return (
    <AppPage.Section title="Tax exemption" size="small" isLoading={isLoading}>
      <Flex.Column gap={32} fullWidth sx={(theme) => ({ color: theme.colors.brand[5] })}>
        <Flex.Row mt={16} gap={32} align="center">
          <RpiText weight="bold">Status</RpiText>
          {!isUndefined(customer?.chargeTax) && <RpiText>{taxStatusLabel}</RpiText>}
        </Flex.Row>

        <RpiText sx={{ maxWidth: 738 }}>
          Are you eligible for tax exemption as a small business? Make sure that your small business is not charged any
          unnecessary taxes by uploading your tax-exempt documents through our online portal. Once we receive them, an
          automated system will notify RPI staff who will immediately begin to review your tax documents.
        </RpiText>

        <RpiButton
          width="fit"
          variant="brand-outline"
          size="small"
          trailingIcon
          onClick={() => navigateRoute(["billing:file-upload"])}
        >
          Upload Tax-Exempt Documents
        </RpiButton>

        {hasTaxDocuments && (
          <MantineProvider theme={rpaTheme}>
            <RpiTableContainer>
              <RpiTable
                isLoading={isLoading || deleteMutation.isLoading}
                perPage={customer?.taxDocuments?.length}
                data={customer?.taxDocuments}
                columns={billingTaxDocumentsColumns}
                additionalColumns={taxDocumentRowMenuColumns}
              />
            </RpiTableContainer>
          </MantineProvider>
        )}
      </Flex.Column>
    </AppPage.Section>
  );
};
